import styled from "@emotion/styled";
import { Flex } from "@radix-ui/themes";
import { Checkbox as CheckBoxRadix } from "radix-ui";
import type React from "react";
import type { ReactNode } from "react";
import { useId } from "react";

import { theme } from "@themes/themes";

import { Icon } from "./Icon";

interface CheckboxProps {
  children?: ReactNode;
  checked: boolean;
  onClick: (v: boolean) => void;
  "data-testid"?: string;
  disabled?: boolean;
  size?: number;
}

export const Checkbox: React.FC<CheckboxProps> = ({
  children,
  checked,
  onClick,
  "data-testid": dataTestId,
  disabled,
  size = 12,
}) => {
  const labelId = useId();

  return (
    <StyledFlex align="center" gap="8px" data-disabled={disabled}>
      <StyledCheckbox
        aria-labelledby={children ? labelId : undefined}
        data-testid={dataTestId}
        checked={checked}
        onCheckedChange={onClick}
        disabled={disabled}
        size={size}
      >
        <StyledIndicator>
          <Icon icon="ri-check-line" size={12} />
        </StyledIndicator>
      </StyledCheckbox>
      <LabelContainer id={labelId} onClick={() => !disabled && onClick(!checked)}>
        {children}
      </LabelContainer>
    </StyledFlex>
  );
};

const StyledFlex = styled(Flex)`
  &[data-disabled="true"] {
    opacity: 0.5;
    cursor: not-allowed;  
    pointer-events: none;
  }
`;

const StyledCheckbox = styled(CheckBoxRadix.Root)<{ size: number }>`
  box-sizing: border-box;
  padding: 0;
  margin: 0;
  border: 2px solid ${theme.color("accent-9")};
  background: transparent;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  min-width: ${({ size }) => size}px;
  min-height: ${({ size }) => size}px;
  flex-shrink: 0;
  border-radius: 3px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 0;

  &[data-state="checked"] {
    background: ${theme.color("accent-9")};
  }

  &:hover {
    cursor: pointer;
  }
`;

const StyledIndicator = styled(CheckBoxRadix.Indicator)`
  color: black;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
  font-weight: bold;
`;

const LabelContainer = styled(Flex)`
  cursor: pointer;
  user-select: none;
`;
